import { useEffect, useRef, useState } from "react";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide, SwiperClass } from "swiper/react";
import { postOfferView } from "../../../../services/offers/offers.service";
import {
  CampaignType,
  Offer,
  OffersIdSlots,
  PlacementStage,
  QueryPostOfferView,
  Template,
  TemplateOptionSettings,
} from "../../../../types/global.types";
import OfferItem from "../offer-item/OfferItem.component";

interface Props {
  settings: TemplateOptionSettings;
  settingsTemplate: Template;
  offersCount: string;
  campaign: CampaignType;
  displayOnly: boolean;
  campaignId: string;
  placementId: string;
  placementName: string;
  isInline?: boolean;
  isSeen: boolean;
  sessionId: string;
  openSingle: (
    o: Offer,
    isSave: boolean,
    isAutoEmail: boolean,
    slot: number
  ) => void;
  onAdd: (offer: Offer, value: boolean, slot: number) => void;
  layoutId: string;
  canAutoSendEmailFromLabel: boolean;
  canAutoSendEmailFromMainCTA: boolean;
  onAutoSendEmailFromLabel: (offer: Offer, slot: number) => Promise<boolean>;
  autoSendingOfferId: string;
}

const SwiperComponent = ({
  settings,
  offersCount,
  campaign,
  displayOnly,
  campaignId,
  placementId,
  placementName,
  settingsTemplate,
  isSeen,
  openSingle,
  onAdd,
  sessionId,
  layoutId,
  canAutoSendEmailFromLabel,
  canAutoSendEmailFromMainCTA,
  onAutoSendEmailFromLabel,
  autoSendingOfferId,
}: Props) => {
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const [eventSentOnIndex, setEventSentOnIndex] = useState<number[]>([]);
  const [eventOfferIds, setEventOfferIds] = useState<OffersIdSlots[]>([]);

  const refNavPrev = useRef<HTMLDivElement | null>(null);
  const refNavNext = useRef<HTMLDivElement | null>(null);
  const lastTrackedRealIndexRef = useRef(0);
  const [showEmailSentToast, setShowEmailSentToast] = useState(false);
  const toastTimeoutRef = useRef<number | null>(null);

  const showEmailToast = () => {
    setShowEmailSentToast(true);
    if (toastTimeoutRef.current) {
      window.clearTimeout(toastTimeoutRef.current);
    }
    toastTimeoutRef.current = window.setTimeout(() => {
      setShowEmailSentToast(false);
    }, 2000);
  };

  const handleAutoSendWithToast = async (offer: Offer, slot: number) => {
    const success = await onAutoSendEmailFromLabel(offer, slot);
    if (success) {
      showEmailToast();
    }
  };

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        window.clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const setDefaultOfferImpressionEvent = async (offers: OffersIdSlots[]) => {
    const data: QueryPostOfferView = {
      offers,
      campaignId,
      campaignName: campaign.name || "",
      companyId: campaign.company_Id,
      placementId,
      placementName,
      pageUrl: "",
      templateId: settingsTemplate.identifier,
      pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
      layoutId,
    };

    await postOfferView(data);
  };

  //set event for default active slide offers
  useEffect(() => {
    if (swiper && isSeen) {
      const _ids = campaign.offers
        .map((offer: Offer, index: number) => ({
          id: offer.id,
          slot: index + 1,
        }))
        .slice(0, Number(settings?.mainLayout.rowsNumber || "1"));
      setDefaultOfferImpressionEvent(_ids);
      setEventOfferIds(_ids);
      setEventSentOnIndex([0]);
    }
    // eslint-disable-next-line
  }, [swiper, isSeen]);

  useEffect(() => {
    const handleKeyUp = (e: KeyboardEvent) => {
      if (!swiper) return;
      if (e.key === "ArrowLeft") {
        swiper.slidePrev();
      } else if (e.key === "ArrowRight") {
        swiper.slideNext();
      }
    };
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [swiper]);

  //geting slides based on campaign offers length and slides number of offers
  const numberOfSlides =
    campaign.offers.length % Number(settings?.mainLayout.rowsNumber || "1") ===
      0
      ? campaign.offers.length / Number(settings?.mainLayout.rowsNumber || "1")
      : campaign.offers.length /
      Number(settings?.mainLayout.rowsNumber || "1") +
      1;
  const getOffersOfSlide = (i: number) =>
    i === 0
      ? campaign.offers.slice(
        i,
        i + Number(settings?.mainLayout.rowsNumber || "1")
      )
      : campaign.offers.slice(
        i * Number(settings?.mainLayout.rowsNumber || "1"),
        i * Number(settings?.mainLayout.rowsNumber || "1") +
        Number(settings?.mainLayout.rowsNumber || "1")
      );

  //set offer view events based on slides that are displayed on active index
  const setOfferViewEvent = (activeIndex: number) => {
    const offersLength = campaign.offers.length;

    if (
      offersLength -
      Number(settings?.mainLayout.rowsNumber || "1") * activeIndex >=
      Number(settings?.mainLayout.rowsNumber || "1")
    ) {
      const viewedOffersId = campaign.offers
        .slice(
          activeIndex * Number(settings?.mainLayout.rowsNumber || "1"),
          activeIndex * Number(settings?.mainLayout.rowsNumber || "1") +
          Number(settings?.mainLayout.rowsNumber || "1")
        )
        .map((offer: Offer, index: number) => ({
          id: offer.id,
          slot:
            activeIndex * Number(settings?.mainLayout.rowsNumber || "1") +
            (index + 1),
        }));

      if (!eventSentOnIndex.includes(activeIndex)) {
        setDefaultOfferImpressionEvent(viewedOffersId);
        setEventOfferIds((prevState) => [...prevState, ...viewedOffersId]);
        setEventSentOnIndex((prevState) => [...prevState, activeIndex]);
      }
    } else {
      const viewedOffersId = campaign.offers
        .slice(
          activeIndex * Number(settings?.mainLayout.rowsNumber || "1"),
          activeIndex * Number(settings?.mainLayout.rowsNumber || "1") +
          (offersLength -
            Number(settings?.mainLayout.rowsNumber || "1") * activeIndex)
        )
        .map((offer: Offer, index: number) => ({
          id: offer.id,
          slot:
            activeIndex * Number(settings?.mainLayout.rowsNumber || "1") +
            (index + 1),
        }));
      if (!eventSentOnIndex.includes(activeIndex)) {
        setDefaultOfferImpressionEvent(viewedOffersId);
        setEventOfferIds((prevState) => [...prevState, ...viewedOffersId]);
        setEventSentOnIndex((prevState) => [...prevState, activeIndex]);
      }
    }
  };

  return (
    <>
      {campaign?.offers.length > 0 && isSeen && (
        <>
          {showEmailSentToast && (
            <div className="template-email-toast">Email sent successfully</div>
          )}
          <Swiper
            className={`swiper-container`}
            modules={[Navigation, Pagination]}
            pagination={{
              clickable: true,
              el: ".swiper-pagination",
            }}
            slidesPerView={1}
            spaceBetween={20}
            watchSlidesProgress={true}
            loop={true}
            onSwiper={(s: SwiperClass) => {
              setSwiper(s);
              lastTrackedRealIndexRef.current = s.realIndex;
            }}
            navigation={{
              prevEl: refNavPrev.current,
              nextEl: refNavNext.current,
            }}
            onBeforeInit={(swiper: any) => {
              swiper.params.navigation.prevEl = refNavPrev.current;
              swiper.params.navigation.nextEl = refNavNext.current;
            }}
            onTransitionEnd={(_swiper: SwiperClass) => {
              const activeIndex = _swiper.realIndex;
              if (activeIndex === lastTrackedRealIndexRef.current) return;
              lastTrackedRealIndexRef.current = activeIndex;
              setOfferViewEvent(activeIndex);
            }}
          >
            {Array.from({ length: numberOfSlides }).map(
              (_: unknown, i: number) => {
                return (
                  <SwiperSlide
                    key={`slide${i}`}
                    style={{ color: settings?.dealLayout.textColor }}
                  >
                    <OfferItem
                      key={`offeritem${i}`}
                      eventOfferIds={eventOfferIds}
                      offers={getOffersOfSlide(i)}
                      settings={settings}
                      settingsTemplate={settingsTemplate}
                      onAdd={onAdd}
                      displayOnly={displayOnly}
                      campaign={campaign}
                      retailerId={campaign?.company_Id}
                      campaignId={campaignId}
                      placementId={placementId}
                      placementName={placementName}
                      openSingle={openSingle}
                      otherSettings={settings.otherSettings}
                      sessionId={sessionId}
                      layoutId={layoutId}
                      layoutOptions={settings.layoutOptions}
                      canAutoSendEmailFromLabel={canAutoSendEmailFromLabel}
                      canAutoSendEmailFromMainCTA={canAutoSendEmailFromMainCTA}
                      onAutoSendEmailFromLabel={handleAutoSendWithToast}
                      autoSendingOfferId={autoSendingOfferId}
                    />
                  </SwiperSlide>
                );
              }
            )}

            <div className="swiper-navigation">
              <div
                className="swiper-button-prev"
                ref={refNavPrev}
                tabIndex={0}
                role="button"
                aria-label="Previous offers"
                onKeyDown={(e) => {
                  if (e.key === "ArrowLeft") {
                    swiper?.slidePrev();
                  }

                  if (e.key === "Enter") {
                    swiper?.slidePrev();
                  }
                }}
              >
                <svg
                  width="34"
                  height="34"
                  viewBox="0 0 34 34"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="18.6094"
                    y="23"
                    width="8.6192"
                    height="1.72384"
                    rx="0.86192"
                    transform="rotate(-135 18.6094 23)"
                    fill={settings.mainLayout.textColor}
                  />
                  <rect
                    x="20.3135"
                    y="12.2188"
                    width="8.6192"
                    height="1.72384"
                    rx="0.86192"
                    transform="rotate(135 20.3135 12.2188)"
                    fill={settings.mainLayout.textColor}
                  />
                </svg>
              </div>
              <div className="swiper-pagination"></div>
              <div
                className="swiper-button-next"
                ref={refNavNext}
                tabIndex={0}
                role="button"
                aria-label="Next offers"
                onKeyDown={(e) => {
                  if (e.key === "ArrowRight") {
                    swiper?.slideNext();
                  }

                  if (e.key === "Enter") {
                    swiper?.slideNext();
                  }
                }}
              >
                <svg
                  width="34"
                  height="34"
                  viewBox="0 0 34 34"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="15.3906"
                    y="11"
                    width="8.6192"
                    height="1.72384"
                    rx="0.86192"
                    transform="rotate(45 15.3906 11)"
                    fill={settings.mainLayout.textColor}
                  />
                  <rect
                    x="14.1719"
                    y="22.2656"
                    width="8.6192"
                    height="1.72384"
                    rx="0.86192"
                    transform="rotate(-45 14.1719 22.2656)"
                    fill={settings.mainLayout.textColor}
                  />
                </svg>
              </div>
            </div>
          </Swiper>
        </>
      )}
    </>
  );
};

export default SwiperComponent;
